import api, { type WiseFriendAxiosRequestConfig } from '../httpClient';
import { clearAuthToken, getAuthGeneration } from '../../utils/auth';
import { dispatchUnauthorizedAuthEvent } from '../../utils/authSession';

type UserProfileUpdatePayload = {
  profile_color?: string | null;
  phone_number?: string | null;
  sms_opted_out_at?: string | null;
  timezone?: string;
  location?: string;
  headline?: string;
  tags?: string[];
  bio?: string;
  fun_facts?: string[];
  working_on?: string[];
  dislikes?: string[];
  boundaries?: string[];
  languages?: string[];
  response_time_blurb?: string;
  preferred_contact_blurb?: string;
  avatar_key?: string | null;
  settings?: {
    meeting_notifications_enabled?: boolean;
    meeting_notification_channel?: 'email' | 'sms' | 'email_sms';
    meeting_notification_lead_minutes?: number;
    in_app_meeting_reminders_enabled?: boolean;
    mentorship_recap_notifications_enabled?: boolean;
    question_flow_tts_enabled?: boolean;
    question_flow_tts_available?: boolean;
    voice_mode_voice?: string;
    voice_mode_personality?: string;
    voice_mode_response_length?: string;
    voice_mode_follow_up_intensity?: string;
  };
};

export const user = {
  getMe: (options: { expectedAnonymousProfileProbe?: boolean } = {}) => {
    const config: WiseFriendAxiosRequestConfig = options.expectedAnonymousProfileProbe
      ? { wiseFriendAuth: { expectedAnonymousProfileProbe: true } }
      : {};
    return api.get('/api/user/me/', config).then((response) => response.data);
  },
  getById: (userId: string) => api.get(`/api/user/${userId}/`).then((response) => response.data),
  getCohortMembers: () => api.get('/api/user/cohort-members/').then((response) => response.data),
  updateProfile: (payload: Partial<UserProfileUpdatePayload>) =>
    api.patch('/api/user/me/', payload).then((response) => response.data),
  changePassword: ({
    currentPassword,
    newPassword,
  }: {
    currentPassword?: string;
    newPassword: string;
  }) => {
    const body: Record<string, string> = {
      new_password: newPassword,
    };
    if (typeof currentPassword === 'string' && currentPassword.length > 0) {
      body.current_password = currentPassword;
    }

    const requestGeneration = getAuthGeneration();
    return api.post('/api/user/change-password/', body).then((response) => {
      if (
        response.data?.reauthentication_required === true &&
        requestGeneration === getAuthGeneration()
      ) {
        clearAuthToken();
        try {
          localStorage.removeItem('user');
        } catch {
          // Continue signing out even when the browser cache is unavailable.
        }
        dispatchUnauthorizedAuthEvent({ reason: 'password_changed', source: 'changePassword' });
      }
      return response.data;
    });
  },
  updateTimezone: (timezone: string, offset: number, options: { onlyIfDefault?: boolean } = {}) =>
    api
      .post('/api/user/timezone/', { timezone, offset, ...(options.onlyIfDefault ? { only_if_default: true } : {}) })
      .then((response) => response.data as { timezone: string; offset: number; applied?: boolean }),
  updateDebugMode: (enabled: boolean) =>
    api
      .patch('/api/user/debug-mode/', { debug_enabled: enabled })
      .then((response) => response.data as { debug_enabled: boolean }),
  updateAdminNote: (userId: string | number, adminNote: string) =>
    api.patch(`/api/admin/user-note/${userId}/`, { admin_note: adminNote }).then((response) => response.data),
};
